import { Tab, Tabs } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAtom } from 'jotai'
import PersonIcon from '@mui/icons-material/Person'
import LocalPhoneIcon from '@mui/icons-material/LocalPhone'
import EmailIcon from '@mui/icons-material/Email'
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'
import { updateModal } from '../../store/common/common_action'
import { lrSearchModeAtom, searchLrDataAtom, selectLrAtom, showHeaderAtom } from '../common/jotaiState/lrSearchAtoms'
import { getBaseGSTInvoiceURL, UNIT_BILLED } from '../common/constants'
import { AllPaymentList } from '../common/paymentsMode'
import { LrTrackCommonControls } from '../lrSearch/lrTrackControls'
import { IdAndImagePreview, ImagePreiewPopup, Item } from '../common/snippets'
import DropDown from '../common/dropDown'
import Modal from '../common/modal'
import { HANS_COMPANY_ID, STA_COMPANY_ID } from '../../helper/companyIdhardcode'
import { companySettingsAtom } from '../common/jotaiState/commonStateAtoms'
import { useTranslation } from '../../i18n/I18nProvider'
import {
  getHamaliChargeLabel,
  getHorizontalTabLabels,
  getPickupCartageLabel,
  getRateSuffix,
  getStaStatusLabel,
  getValuationLabel,
} from './lrTrackingPopupConstants'

const horizontalTabIds = (index) => ({
  id: `horizontal-tab-${index}`,
  'aria-controls': `horizontal-tabpanel-${index}`,
})

const TRACKING_TAB_INDEX = 0
const DETAILS_TAB_INDEX = 1
const CONSIGNMENT_TAB_INDEX = 2

const textOf = (value) => (value === null || value === undefined ? '' : String(value))

const joinRows = (rows, pick, separator) => rows.map((row) => textOf(pick(row))).join(separator)

const displayOrDash = (value) => {
  if (value === 0 || value === '0') return '0'
  if (value === null || value === undefined) return '-'
  const text = String(value).trim()
  if (text === '' || /^[,+\s]+$/.test(text)) return '-'
  return String(value)
}

const formatEwayBill = (raw) => {
  if (!raw) return ''
  return String(raw).split('~')[0].split(',').join(',\n')
}

const paymentTypeLabel = (lrInfo, useToPayAsCOD) => {
  const paymentIndex = +lrInfo?.PaymentType || 0
  let payment = AllPaymentList[paymentIndex]
  if (useToPayAsCOD === 1) {
    if (paymentIndex === 2) payment = 'COD'
    if (paymentIndex === 7) payment = 'COD_MANUAL'
  }
  return payment
}

const buildConsignmentFields = (rows, translations) => {
  let unitSum = 0
  let totalFreight = 0
  rows.forEach((row) => {
    unitSum += parseInt(row.Quantity, 10) || 0
    totalFreight += parseInt(row.Freight, 10) || 0
  })
  return {
    consignmentType: joinRows(rows, (row) => row.SubType, ', '),
    mopName: joinRows(rows, (row) => row.MOPName, ', '),
    description: joinRows(rows, (row) => row.Description, ','),
    totalUnits: joinRows(rows, (row) => row.Quantity, '+'),
    unitSum,
    goodsValue: joinRows(rows, (row) => row.GoodsValue, ', '),
    actualWeight: joinRows(rows, (row) => (textOf(row.ActualWeight) === '' ? '' : `${row.ActualWeight}KG`), ', '),
    chargedWeight: joinRows(rows, (row) => (textOf(row.ChargedWeight) === '' ? '' : `${row.ChargedWeight}KG`), ', '),
    allRate: joinRows(
      rows,
      (row) => (textOf(row.Rate) === '' ? '' : `${row.Rate}${getRateSuffix(translations, row.BillingUnit === UNIT_BILLED)}`),
      ', '
    ),
    allFreights: joinRows(rows, (row) => row.Freight, ','),
    totalFreight,
    allSize: joinRows(rows, (row) => `${row.Length || 0} X ${row.Width || 0} X ${row.Height || 0}`, ', '),
    allWeightVolume: joinRows(rows, (row) => row.VolumeWeight || 0, ' ,'),
  }
}

const firstValue = (...values) => {
  for (const value of values) {
    if (value === 0 || value === '0') return '0'
    if (value !== null && value !== undefined && String(value).trim() !== '') return String(value).trim()
  }
  return '-'
}

const trackingRowStyle = (type) => {
  if (type === 'B') return { background: 'rgba(200, 240, 212, 0.5)' }
  if (type === 'D') return { background: 'rgba(255, 217, 168, 0.5)' }
  if (type === 'R') return { background: 'rgba(197, 220, 255, 0.5)' }
  if (type === 'F') return { background: 'rgba(231, 245, 168, 0.5)' }
  if (type === 'T') return { background: 'rgba(224, 212, 255, 0.5)' }
  if (type === 'G') return { background: 'rgba(184, 239, 230, 0.5)' }
  if (type === 'L') return { background: 'rgba(255, 201, 201, 0.5)' }
  return { background: 'rgba(243, 243, 243, 0.5)' }
}

const withExtraLine = (value, extra) => {
  const line = textOf(extra).trim()
  if (!line) return value
  if (!value || value === '-') return line
  return `${value}\n${line}`
}

const driverLine = (event) => {
  const name = textOf(event?.DriverConductorName).trim()
  const number = textOf(event?.ContactNo).trim()
  return [name, number].filter(Boolean).join(' / ')
}

const formatTrackingStatus = (event, translations, defaultCurrencyID) => {
  const labels = translations?.lrTrackingPopup || {}
  if (event?.TYPE === 'L') return labels.lost || 'Lost'
  let status = textOf(event?.TypeDet)
  if (status.endsWith('-App')) status = status.slice(0, -4)
  if (Number(defaultCurrencyID) === 2) status = getStaStatusLabel(status, translations)
  if (event?.TYPE === 'D' && Number(event?.IsShortReceived) === 1) {
    status = withExtraLine(status, labels.shortReceived || 'Short Received')
  }
  return firstValue(status)
}

const mapTrackingRow = (event, translations, defaultCurrencyID) => {
  const type = event?.TYPE
  const status = formatTrackingStatus(event, translations, defaultCurrencyID)
  const date = firstValue(event?.DTTime, event?.EventDateTime)
  if (type === 'B') {
    return {
      type,
      status,
      date,
      from: firstValue(event?.FromCity, event?.SourceCity),
      to: firstValue(event?.ToCity, event?.DestinationCity),
      fromBranch: firstValue(event?.BranchName, event?.SourceBranch),
      toBranch: firstValue(event?.ToBranchName, event?.DestinationBranch),
      byUser: firstValue(event?.TransactionByUser, event?.BookedByUser),
      vehicle: '-',
    }
  }
  if (type === 'D') {
    return {
      type,
      status,
      date,
      from: firstValue(event?.FromCity, event?.DispatchedFromCity),
      to: firstValue(event?.ToCity, event?.DispatchedToCity),
      fromBranch: firstValue(event?.DispatchedFromBranch),
      toBranch: firstValue(event?.BranchName, event?.DispatchedToBranch),
      byUser: firstValue(event?.TransactionByUser, event?.DispatchedByUser),
      vehicle: withExtraLine(firstValue(event?.VehicleNo), driverLine(event)),
    }
  }
  if (type === 'R') {
    return {
      type,
      status,
      date,
      from: firstValue(event?.FromCity, event?.DispatchedBy),
      to: firstValue(event?.ToCity, event?.ReceivedBy),
      fromBranch: firstValue(event?.DispatchedFromBranch),
      toBranch: firstValue(event?.BranchName, event?.ReceivedByBranch),
      byUser: firstValue(event?.TransactionByUser, event?.ReceivedByUser),
      vehicle: withExtraLine('-', driverLine(event)),
    }
  }
  if (type === 'T') {
    return {
      type,
      status,
      date,
      from: firstValue(event?.FromCity, event?.DispatchedFromCity),
      to: firstValue(event?.ToCity, event?.DispatchedToCity),
      fromBranch: firstValue(event?.BranchName),
      toBranch: firstValue(event?.ToBranchName),
      byUser: firstValue(event?.TransactionByUser),
      vehicle: firstValue(event?.VehicleNo),
    }
  }
  if (type === 'G') {
    return {
      type,
      status,
      date,
      from: firstValue(event?.FromCity, event?.DispatchedBy),
      to: firstValue(event?.ToCity, event?.ReceivedBy),
      fromBranch: firstValue(event?.BranchName),
      toBranch: firstValue(event?.ToBranchName, event?.ReceivedByBranch),
      byUser: firstValue(event?.TransactionByUser, event?.ReceivedByUser),
      vehicle: withExtraLine(firstValue(event?.VehicleNo), driverLine(event)),
    }
  }
  if (type === 'F') {
    const deliveryExtra = [textOf(event?.Remarks).trim(), textOf(event?.ReceiverMobileNo).trim()].filter(Boolean).join('\n')
    return {
      type,
      status,
      date,
      from: firstValue(event?.ToCity),
      to: firstValue(event?.ReceivedBy),
      fromBranch: firstValue(event?.BranchName),
      toBranch: '-',
      byUser: withExtraLine(firstValue(event?.TransactionByUser), deliveryExtra),
      vehicle: firstValue(event?.VehicleNo),
    }
  }
  if (type === 'L') {
    return {
      type,
      status,
      date,
      from: firstValue(event?.FromCity, event?.SourceCity),
      to: firstValue(event?.DestinationCity),
      fromBranch: firstValue(event?.SourceBranch),
      toBranch: firstValue(event?.DestinationBranch),
      byUser: firstValue(event?.MarkAsLostByUser),
      vehicle: '-',
    }
  }
  return {
    type,
    status,
    date,
    from: firstValue(event?.FromCity),
    to: firstValue(event?.ToCity),
    fromBranch: firstValue(event?.BranchName),
    toBranch: firstValue(event?.ToBranchName),
    byUser: firstValue(event?.TransactionByUser),
    vehicle: firstValue(event?.VehicleNo),
  }
}

const trackingColumns = [
  { key: 'status', label: 'Status' },
  { key: 'date', label: 'Date and Time' },
  { key: 'from', label: 'From' },
  { key: 'to', label: 'To' },
  { key: 'fromBranch', label: 'From Branch' },
  { key: 'toBranch', label: 'To Branch' },
  { key: 'byUser', label: 'By / Booked By / Dispatched By' },
  { key: 'vehicle', label: 'Vehicle' },
]

const cornerRadius = (isFirst, isLast, edge) => {
  if (edge === 'top' && isFirst) return { borderTopLeftRadius: '6px' }
  if (edge === 'top' && isLast) return { borderTopRightRadius: '6px' }
  if (edge === 'bottom' && isFirst) return { borderBottomLeftRadius: '6px' }
  if (edge === 'bottom' && isLast) return { borderBottomRightRadius: '6px' }
  return {}
}

const HorizontalTrackingTable = ({ events }) => {
  const { translations } = useTranslation()
  const [companySettings] = useAtom(companySettingsAtom)
  const defaultCurrencyID = companySettings?.DefaultCurrencyID || 0
  const rows = (Array.isArray(events) ? events : []).map((event) => mapTrackingRow(event, translations, defaultCurrencyID))
  const lastColumn = trackingColumns.length - 1
  return (
    <div style={{ height: '100%', overflow: 'auto', padding: '50px 15px 15px 15px', boxSizing: 'border-box' }}>
      <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontFamily: 'Roboto, sans-serif', fontSize: '13px', fontWeight: 700 }}>
        <thead>
          <tr>
            {trackingColumns.map((column, columnIndex) => (
              <th
                key={column.key}
                style={{
                  position: 'sticky',
                  top: 0,
                  background: '#0086ff',
                  color: '#fff',
                  fontWeight: 700,
                  textAlign: 'left',
                  padding: '10px 12px',
                  whiteSpace: 'nowrap',
                  ...cornerRadius(columnIndex === 0, columnIndex === lastColumn, 'top'),
                  ...(rows.length === 0 ? cornerRadius(columnIndex === 0, columnIndex === lastColumn, 'bottom') : {}),
                }}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => {
            const tone = trackingRowStyle(row.type)
            const isLastRow = index === rows.length - 1
            return (
              <tr key={`${row.status}-${index}`}>
                {trackingColumns.map((column, columnIndex) => (
                  <td
                    key={column.key}
                    style={{
                      padding: '12px',
                      background: tone.background,
                      color: '#444',
                      fontWeight: 700,
                      verticalAlign: 'top',
                      whiteSpace: 'pre-line',
                      ...(isLastRow ? cornerRadius(columnIndex === 0, columnIndex === lastColumn, 'bottom') : {}),
                    }}
                  >
                    {row[column.key]}
                  </td>
                ))}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const HorizontalLrDetails = ({ lrInfo, LrTrackData }) => {
  const { translations } = useTranslation()
  const lt = translations?.lrTrackingPopup || {}
  const [idDetailsPreview, setIdDetailPreview] = useState(false)
  const [idDetailsProps, setIdDetailsProps] = useState(false)
  const [personImagePreview, setPersonImagePreview] = useState(false)
  const [personImageProps, setPersonImageProps] = useState(false)
  const [bookingInvLink, setBookingInvLink] = useState('')
  const [deliveryInvLink, setDeliveryInvLink] = useState('')
  const firstStatus = LrTrackData ? LrTrackData?.[0] : false
  const lastStatus = LrTrackData?.length ? LrTrackData[LrTrackData.length - 1] : false
  const showSenIdDrop = lrInfo?.SenderIDProof || lrInfo?.SenderIDProofNo || firstStatus?.IDProofImageURL || false
  const showsenImage = firstStatus?.SenderImageURL || false
  const showRecIdDrop = lrInfo?.ReceiverIDProof || lrInfo?.ReceiverIDProofNo || lastStatus?.IDProofImageURL || false
  const showRecImage = lastStatus?.SenderImageURL || false

  useEffect(() => {
    if (lrInfo?.BookingID !== undefined && lrInfo?.BookingID !== null) {
      setBookingInvLink(getBaseGSTInvoiceURL(lrInfo?.BookingID, 'B', lrInfo?.BookingCompanyID))
      setDeliveryInvLink(getBaseGSTInvoiceURL(lrInfo?.BookingID, 'D', lrInfo?.DeliveryCompanyID))
    }
  }, [lrInfo])

  const setPopupIdDetails = (isSender = true) => {
    const data = {
      updateModal: setIdDetailPreview,
      headTxt: lt.idProofPreview || 'Preview',
      idTypeLabel: lt.idType || 'ID Type',
      idLabel: lt.idNumber || 'ID Number',
    }
    if (isSender) {
      data.imgUrl = firstStatus?.IDProofImageURL
      data.idValue = lrInfo?.SenderIDProofNo
      data.idTypeValue = lrInfo?.SenderIDProof
    } else {
      data.imgUrl = lastStatus?.IDProofImageURL
      data.idValue = lrInfo?.ReceiverIDProofNo
      data.idTypeValue = lrInfo?.ReceiverIDProof
    }
    setIdDetailsProps(data)
    setIdDetailPreview(true)
  }

  const showImagePreviewPopup = (isSender = true) => {
    const data = {
      updateModal: setPersonImagePreview,
      data: { hideControl: true },
    }
    data.data.src = isSender ? firstStatus?.SenderImageURL : lastStatus?.SenderImageURL
    setPersonImageProps(data)
    setPersonImagePreview(true)
  }

  return (
    <div className="detailed-tabs horizontal-lr-details">
      <div className="tab1-view" style={{ padding: '50px 15px 15px 15px' }}>
      <div className="lr-details-cards">
        <div className="lr-details-card">
            <div className="horizontal-source-details">
            <div className="heading-tab" >{lt.source || 'Source'}: {lrInfo?.FromCity}</div>
            <div className="tab-details" style={{paddingLeft: '5px'}}>{lt.branch || 'Branch'}: {lrInfo?.BkgBranchName}</div>
            <div className="tab-details" >
              <PersonIcon />
              {lt.senderName || 'Sender Name'}:{lrInfo?.Sender}
            </div>
            <div className="tab-details">
              <LocalPhoneIcon />
              {lt.senderNo || 'Sender No'}:{lrInfo?.SenderMobileNo}
            </div>
            {textOf(lrInfo?.SenderEmailID).trim() !== '' && (
              <div className="tab-details">
                <EmailIcon />
                {lt.senderEmail || 'Sender Email'}: {lrInfo?.SenderEmailID}
              </div>
            )}
            {textOf(lrInfo?.SenderGSTN).trim() !== '' && (
              <div className="tab-details">
                <CurrencyRupeeIcon />
                {lt.senderGstn || 'Sender GSTN'}:{lrInfo?.SenderGSTN}
              </div>
            )}
            {showSenIdDrop && (
              <Item sx={{ width: '100%' }}>
                <DropDown
                  disabled={false}
                  type="file"
                  defaultvalue={`${lrInfo?.SenderIDProof || lt.noIdType || 'No ID Type'} | ${lrInfo?.SenderIDProofNo || lt.noIdNumber || 'No Id Number'}`}
                  data={[]}
                  actnTxt={lt.show || 'Show'}
                  showActn={true}
                  onClick={setPopupIdDetails}
                />
              </Item>
            )}
            {showsenImage && (
              <Item sx={{ width: '100%' }}>
                <p style={{ display: 'flex', alignItems: 'center', maxWidth: '800px', fontSize: '13px', color: 'black', margin: '0px' }}>
                  <span>{lt.senderImage || 'Sender Image'}:</span>
                  <img
                    src={firstStatus.SenderImageURL}
                    alt=""
                    style={{ height: '50px', width: '50px', objectFit: 'cover', borderRadius: '50%', margin: '0px 10px' }}
                  />
                  <a
                    href=""
                    className="thumbnail blue-color-txt"
                    onClick={(e) => {
                      e.preventDefault()
                      showImagePreviewPopup(true)
                    }}
                  >
                    {lt.show || 'Show'}
                    <span>
                      <img className={'right'} src={firstStatus.SenderImageURL} alt="" />
                    </span>
                  </a>
                </p>
              </Item>
            )}
          </div>
        </div>
        <div className="lr-details-card">
            <div className="horizontal-destination-details">
            <div className="heading-tab">{lt.destination || 'Destination'}: {lrInfo?.ToCity}</div>
            <div className="tab-details">{lt.branch || 'Branch'}: {lrInfo?.DestinationBranch}</div>
            <div className="tab-details">
              <PersonIcon />
              {lt.receiverName || 'Receiver Name'}: {lrInfo?.RecName}
            </div>
            <div className="tab-details">
              <LocalPhoneIcon /> {lt.receiverNo || 'Receiver No'}: {lrInfo?.RecMobileNo}
            </div>
            {textOf(lrInfo?.ReceiverEmailID).trim() !== '' && (
              <div className="tab-details">
                <EmailIcon />
                {lt.receiverEmail || 'Receiver Email'}: {lrInfo?.ReceiverEmailID}
              </div>
            )}
            {textOf(lrInfo?.ReceiverGSTN).trim() !== '' && (
              <div className="tab-details">
                <CurrencyRupeeIcon />
                {lt.receiverGstn || 'Receiver GSTN'}:{lrInfo?.ReceiverGSTN}
              </div>
            )}
            {lastStatus?.TYPE === 'F' && showRecIdDrop && (
              <Item sx={{ width: '100%' }}>
                <DropDown
                  disabled={false}
                  type="file"
                  defaultvalue={`${lrInfo?.ReceiverIDProof || lt.noIdType || 'No ID Type'} | ${lrInfo?.ReceiverIDProofNo || lt.noIdNumber || 'No Id Number'}`}
                  data={[]}
                  actnTxt={lt.show || 'Show'}
                  showActn={true}
                  onClick={() => setPopupIdDetails(false)}
                />
              </Item>
            )}
            {lastStatus?.TYPE === 'F' && showRecImage && (
              <Item sx={{ width: '100%' }}>
                <p style={{ display: 'flex', alignItems: 'center', maxWidth: '800px', fontSize: '13px', color: 'black', margin: '0px' }}>
                  <span>{lt.receiverImage || 'Receiver Image'}:</span>
                  <img
                    src={lastStatus.SenderImageURL}
                    alt=""
                    style={{ height: '50px', width: '50px', objectFit: 'cover', borderRadius: '50%', margin: '0px 10px' }}
                  />
                  <a
                    href=""
                    className="thumbnail blue-color-txt"
                    onClick={(e) => {
                      e.preventDefault()
                      showImagePreviewPopup(false)
                    }}
                  >
                    {lt.show || 'Show'}
                    <span>
                      <img className={'right'} src={lastStatus.SenderImageURL} alt="" />
                    </span>
                  </a>
                </p>
              </Item>
            )}
          </div>
        </div>
      </div>
      {lrInfo?.IsBookingInvoice === 1 && (
        <div className="booking-gst-inv-details">
          <div style={{ fontWeight: 'bold' }}>{`${lt.bookingGstInvoice || 'Booking GST Invoice'}: ${lrInfo?.BookingInvoiceNo}`}</div>
          <div>
            <a href={bookingInvLink} style={{ color: 'blue', textDecoration: 'underline' }} target="_blank" rel="noreferrer">
              {lt.invoiceLink || 'Invoice Link'}
            </a>
          </div>
        </div>
      )}
      {lrInfo?.IsDeliveryInvoice === 1 && (
        <div className="booking-gst-inv-details">
          <div style={{ fontWeight: 'bold' }}>{`${lt.deliveryGstInvoice || 'Delivery GST Invoice'}: ${lrInfo?.DeliveryInvoiceNo}`}</div>
          <div>
            <a href={deliveryInvLink} style={{ color: 'blue', textDecoration: 'underline' }} target="_blank" rel="noreferrer">
              {lt.invoiceLink || 'Invoice Link'}
            </a>
          </div>
        </div>
      )}
      {idDetailsPreview && <IdAndImagePreview {...idDetailsProps} />}
      {personImagePreview && <ImagePreiewPopup {...personImageProps} />}
      </div>
    </div>
  )
}

const HorizontalConsignmentDetails = ({ consignmentDetails, lrInfo, loginData }) => {
  const { translations } = useTranslation()
  const lt = translations?.lrTrackingPopup || {}
  const [companySettings] = useAtom(companySettingsAtom)
  const { UseToPayAsCOD = 0, DefaultCurrencyID = 0 } = companySettings || {}
  const rows = Array.isArray(consignmentDetails) ? consignmentDetails : []
  const fields = buildConsignmentFields(rows, translations)
  const companyId = loginData?.intCompanyID
  const isHans = [HANS_COMPANY_ID].includes(companyId)
  const isSta = companyId === STA_COMPANY_ID
  const ewayBill = formatEwayBill(lrInfo?.EWayBillNo)
  const exemptedGoods = lrInfo?.ExemptedGoods || ''
  const goodsText = String(fields.goodsValue ?? '').trim()
  const goodsValue = rows.length && goodsText !== '' && Number(goodsText) === 0 && isSta
    ? lt.asPerCarrierAct || 'As Per Carrier Act'
    : fields.goodsValue
  const hamaliSum = (parseInt(lrInfo?.HamaliCharges, 10) || 0) + (parseInt(lrInfo?.OperatorHamali, 10) || 0)

  const detailCells = [
    { label: lt.paymentType || 'Payment Type', value: paymentTypeLabel(lrInfo, UseToPayAsCOD) },
    { label: lt.invoiceNo || 'Invoice No', value: lrInfo?.PartyBillNo },
    { label: lt.invoiceAmount || 'Invoice Amount', value: lrInfo?.BillAmount },
    { label: lt.consignmentType || 'Consignment Type', value: fields.consignmentType },
    { label: lt.methodOfPacking || 'Method of Packing', value: fields.mopName },
    { label: lt.description || 'Description', value: fields.description },
    { label: lt.cartageRemark || 'Cartage Remark', value: lrInfo?.CartageRemarks },
    { label: lt.totalUnits || 'Total Units', value: rows.length ? `${fields.totalUnits}=${fields.unitSum}` : '' },
    { label: lt.goodsValue || 'Goods Value', value: goodsValue },
    { label: lt.actualWeight || 'Actual Weight', value: fields.actualWeight },
    { label: lt.chargedWeight || 'Charged Weight', value: fields.chargedWeight },
    { label: lt.rate || 'Rate', value: fields.allRate },
  ]
  if (ewayBill || exemptedGoods) {
    detailCells.push({ label: lt.ewayBillNo || 'E-way Bill No', value: ewayBill || exemptedGoods })
  }
  if (isSta) {
    detailCells.push(
      { label: lt.cftWeight || 'CFT Weight', value: fields.allWeightVolume },
      { label: lt.cftSize || 'CFT Size', value: fields.allSize }
    )
  }

  const charges = [
    { label: lt.freight || 'Freight', value: rows.length ? `${fields.allFreights}=${fields.totalFreight}` : '' },
    {
      label: getPickupCartageLabel(translations, companyId),
      value: isHans ? lrInfo?.CartageAmount : lrInfo?.PickupCartage,
    },
  ]
  if (!isHans) {
    charges.push(
      { label: lt.miscCartage || 'Misc Cartage', value: lrInfo?.CommCartage },
      { label: lt.deliveryCartage || 'Delivery Cartage', value: lrInfo?.DeliveryCartage }
    )
  }
  charges.push(
    {
      label: getHamaliChargeLabel(translations, companyId),
      value: lrInfo ? `${lrInfo?.HamaliCharges ?? 0}+${lrInfo?.OperatorHamali ?? 0}=${hamaliSum}` : '',
    },
    { label: getValuationLabel(translations, DefaultCurrencyID), value: lrInfo?.Valuation },
    { label: lt.documentCharge || 'Document Charge', value: lrInfo?.DocumentCharges },
    { label: lt.otherCharges || 'Other Charges', value: lrInfo?.OtherCharge },
    { label: lt.gst || 'GST', value: lrInfo?.ServiceTax },
    { label: lt.totalAmount || 'Total Amount', value: lrInfo?.NetAmount }
  )

  const remainder = detailCells.length % 3

  return (
    <div className="horizontal-consignment-details">
      <div className="consignment-grid">
        {detailCells.map((cell, index) => {
          const isLast = index === detailCells.length - 1
          const spanClass = isLast && remainder === 1 ? 'span-rest-1' : isLast && remainder === 2 ? 'span-rest-2' : ''
          const rowClass = Math.floor(index / 3) % 2 === 0 ? 'consignment-row-dark' : 'consignment-row-misty'
          return (
            <div className="consignment-pair" key={cell.label}>
              <div className={`consignment-cell consignment-label ${rowClass}`}>{cell.label}</div>
              <div className={`consignment-cell consignment-value ${rowClass} ${spanClass}`}>{displayOrDash(cell.value)}</div>
            </div>
          )
        })}
      </div>
      <table className="consignment-charges">
        <thead>
          <tr>
            {charges.map((charge) => (
              <th key={charge.label}>{charge.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {charges.map((charge) => (
              <td key={charge.label}>{displayOrDash(charge.value)}</td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  )
}

export const LrTrackingHorizontalPopupUI = (props) => {
  const {
    controlData,
    roleRights: roleRightsProp,
    lrInfo: lrInfoProp,
    LrTrackData: trackDataProp,
    selectLr: selectLrProp,
    loginData: loginDataProp,
    onClose,
    onCloseCallback,
    embedded = false,
  } = props
  const { translations } = useTranslation()
  const lt = translations?.lrTrackingPopup || {}
  const tabLabels = getHorizontalTabLabels(translations)
  const dispatch = useDispatch()
  const common = useSelector((state) => state?.common)
  const { lrStatus, loginData: reduxLoginData, roleRights: reduxRoleRights } = common || {}
  const [, setShowHeader] = useAtom(showHeaderAtom)
  const [, SetMode] = useAtom(lrSearchModeAtom)
  const [selectLrFromAtom] = useAtom(selectLrAtom)
  const [, setSearchLrData] = useAtom(searchLrDataAtom)
  const [activeTab, setActiveTab] = useState(0)

  const lrInfo = lrInfoProp !== undefined ? lrInfoProp : lrStatus?.Data?.Table?.[0]
  const consignmentDetails = Array.isArray(lrStatus?.Data?.Table) && lrStatus.Data.Table.length
    ? lrStatus.Data.Table
    : lrInfo
      ? [lrInfo]
      : []
  const LrTrackData = trackDataProp !== undefined ? trackDataProp : lrStatus?.Data?.Table1
  const selectLr = selectLrProp !== undefined ? selectLrProp : selectLrFromAtom
  const loginData = loginDataProp !== undefined ? loginDataProp : reduxLoginData
  const roleRights = roleRightsProp !== undefined ? roleRightsProp : reduxRoleRights
  const showFooter = Boolean(controlData && roleRights)
  const lrNumber = selectLr?.LRNo || selectLr?.LRNO || lrInfo?.LRNO || ''

  const modeClose = () => dispatch(updateModal())
  const handleHeaderCancel = () => {
    setShowHeader(true)
    setSearchLrData([])
    dispatch(updateModal())
    SetMode(1)
  }

  const tabBody = (
    <>
      <Tabs
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          background: '#fff',
          '& .MuiTabs-indicator': { backgroundColor: '#0086ff' },
        }}
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        className="lrtrack-tabs"
        variant="fullWidth"
        aria-label="lr tracking, lr details and consignment details"
      >
        {tabLabels.map((label, index) => (
          <Tab
            key={label}
            label={label}
            sx={{
              minWidth: 0,
              whiteSpace: 'nowrap',
              color: '#444',
              '&.Mui-selected': { color: '#0086ff' },
            }}
            {...horizontalTabIds(index)}
          />
        ))}
      </Tabs>
      {tabLabels.map((label, index) => (
        <div
          key={label}
          role="tabpanel"
          hidden={activeTab !== index}
          id={`horizontal-tabpanel-${index}`}
          aria-labelledby={`horizontal-tab-${index}`}
          style={{ flex: 1, minHeight: '120px', overflow: 'auto', background: '#fff' }}
        >
          {index === TRACKING_TAB_INDEX && <HorizontalTrackingTable events={LrTrackData} />}
          {index === DETAILS_TAB_INDEX && <HorizontalLrDetails lrInfo={lrInfo} LrTrackData={LrTrackData} />}
          {index === CONSIGNMENT_TAB_INDEX && (
            <HorizontalConsignmentDetails consignmentDetails={consignmentDetails} lrInfo={lrInfo} loginData={loginData} />
          )}
        </div>
      ))}
      {showFooter && (
        <div className="lrt-card" style={{ background: '#fff', borderTop: '2px solid #ddd' }}>
          <LrTrackCommonControls
            layout="row"
            lrInfo={lrInfo}
            lrStatInfo={LrTrackData}
            loginData={loginData}
            data={controlData}
            roleRights={roleRights}
          />
        </div>
      )}
    </>
  )

  if (embedded) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%', background: '#fff' }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #ddd' }}>
          <div>{tabLabels[activeTab]}</div>
          <div style={{ fontSize: '12px' }}>{(lt.lrNumberPrefix || 'LR Number: ') + lrNumber}</div>
        </div>
        {tabBody}
      </div>
    )
  }

  return (
    <Modal
      headTxt={tabLabels[activeTab]}
      headText2={(lt.lrNumberPrefix || 'LR Number: ') + lrNumber}
      width={'90%'}
      height={'100%'}
      updateModal={onClose || modeClose}
      onCloseCallback={onCloseCallback || (onClose ? undefined : handleHeaderCancel)}
      modalClass="model--container-horizontal"
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 62px)' }}>{tabBody}</div>
    </Modal>
  )
}
